import {useState, useMemo, useEffect} from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Card,
  Chip,
  Drawer,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Toolbar,
  Typography,
  Switch,
  Tooltip,
} from '@material-ui/core';
import {MuiThemeProvider, createMuiTheme} from '@material-ui/core/styles';
import {
  Apps as AppsIcon,
  CallSplit as CallSplitIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Create as CreateIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@material-ui/icons';
import {amber, grey, green, purple, red} from '@material-ui/core/colors';
import {canWrite, canDelete} from 'Sbs/service/SbsService';

const purpleTheme = createMuiTheme({palette: {primary: purple}});
const timestampToString = (timestamp) =>
  timestamp
    ? new Date(timestamp).toLocaleDateString('en-AU', {
        weekday: 'short',
        year: '2-digit',
        month: 'short',
        day: 'numeric',
      })
    : 'unknown';

export default function SbsProperties(props) {
  const {
    sbs,
    parentSbss,
    onDeleteSbs,
    duplicationMode,
    onToggleDuplicationMode,
    duplicationSelectedSteps,
    onToggleSelectAllSteps,
    onDuplicateSteps,
    sbsTotalTime,
    onLoadUnloadAllSubSbs,
    subSbsLoaded,
    hasSubSbs,
  } = props;
  const {
    teams = [],
    tags = [],
    sbsStatus: {status},
  } = sbs;
  const loadUnloadAllSubSbsText = useMemo(
    () => `${subSbsLoaded ? 'Unload All' : 'Load All'} Sub SBS`,
    [subSbsLoaded],
  );
  const [sbsDetails, setSbsDetails] = useState(false);
  const [canWriteSbs, setCanWriteSbs] = useState(false);
  const [canDeleteSbs, setCanDeleteSbs] = useState(false);
  const handleToggleDrawer = () => setSbsDetails((existing) => !existing);

  useEffect(() => {
    const checkPermissions = async () => {
      if (sbs?.id) {
        try {
          const [hasWritePermission, hasDeletePermission] = await Promise.all([
            canWrite(sbs.id),
            canDelete(sbs.id),
          ]);

          setCanWriteSbs(hasWritePermission === true);
          setCanDeleteSbs(hasDeletePermission === true);
        } catch (error) {
          console.error('Error checking permissions:', error);
          setCanWriteSbs(false);
          setCanDeleteSbs(false);
        }
      } else {
        setCanWriteSbs(false);
        setCanDeleteSbs(false);
      }
    };

    checkPermissions();
  }, [sbs?.id]);
  const statusColour = useMemo(() => {
    switch (status) {
      case 'Completed':
        return green['500'];
      case 'WIP':
        return amber['500'];
      case 'Kaizen':
        return grey['500'];
      case 'Outdated':
        return red['500'];
      default:
        return 'inherit';
    }
  }, [status]);

  return (
    <Card
      style={{paddingTop: 8, position: 'sticky', top: 64, zIndex: 100}}
      className="printPositionStatic "
    >
      <Toolbar>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          <div
            style={{
              flex: 5,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              justifyContent: 'center',
              marginRight: 8,
            }}
          >
            <Typography variant="h6">{sbs.title}</Typography>
            <Typography variant="caption">
              {`Last modified by ${
                sbs.modifiedBy.fullName
              } on ${timestampToString(sbs.modifiedDt)}`}
            </Typography>
            <Typography align="center">
              {`Total SBS Time: ${sbsTotalTime}`}
            </Typography>
          </div>
          <Chip
            label={sbs.sbsStatus.status}
            style={{marginRight: '8px', backgroundColor: statusColour}}
          />
        </div>
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}
          className="printDisplayNone"
        >
          <MuiThemeProvider theme={purpleTheme}>
            {onToggleDuplicationMode && (
              <FormControlLabel
                control={
                  <Switch
                    checked={duplicationMode}
                    onChange={onToggleDuplicationMode}
                    color="primary"
                  />
                }
                label="duplication mode"
                style={{marginLeft: 8}}
              />
            )}

            {duplicationMode && (
              <>
                <Tooltip
                  title={
                    duplicationSelectedSteps.length === 0
                      ? 'Select All Steps'
                      : 'Deselect All Steps'
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={onToggleSelectAllSteps}
                      style={{marginLeft: 8}}
                      disabled={!onToggleSelectAllSteps}
                      startIcon={
                        duplicationSelectedSteps.length === 0 ? (
                          <CheckBoxOutlineBlankIcon />
                        ) : (
                          <CheckBoxIcon />
                        )
                      }
                    >
                      {duplicationSelectedSteps.length === 0
                        ? 'select: all'
                        : 'deselect: all'}
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip
                  title={
                    duplicationSelectedSteps.length === 0
                      ? 'Select Steps To Duplicate'
                      : 'Duplicate Steps'
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={onDuplicateSteps}
                      style={{marginLeft: 8}}
                      disabled={
                        !onDuplicateSteps ||
                        duplicationSelectedSteps.length === 0
                      }
                      startIcon={<CallSplitIcon />}
                    >
                      duplicate: steps
                    </Button>
                  </span>
                </Tooltip>
              </>
            )}
          </MuiThemeProvider>

          {!duplicationMode && (
            <>
              {hasSubSbs && (
                <Tooltip
                  title={
                    subSbsLoaded ? 'Collapse All Sub SBS' : 'Expand All Sub SBS'
                  }
                >
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={onLoadUnloadAllSubSbs}
                    style={{width: 205}}
                    startIcon={
                      subSbsLoaded ? <ExpandLessIcon /> : <ExpandMoreIcon />
                    }
                  >
                    {loadUnloadAllSubSbsText}
                  </Button>
                </Tooltip>
              )}
              <Tooltip
                title={
                  !canDeleteSbs
                    ? 'You do not have delete permission for this SBS'
                    : 'Delete SBS'
                }
              >
                <span>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={onDeleteSbs}
                    style={{marginLeft: 8}}
                    disabled={!onDeleteSbs || !canDeleteSbs}
                    startIcon={<DeleteIcon />}
                  >
                    delete: sbs
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  !canWriteSbs
                    ? 'You do not have write permission for this SBS'
                    : 'Edit SBS'
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="primary"
                    href={`/#/Sbss/${sbs.id}/update`}
                    style={{marginLeft: 8}}
                    disabled={!canWriteSbs}
                    startIcon={<CreateIcon />}
                  >
                    edit: sbs
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          <Tooltip title="View SBS Details">
            <Button
              variant="outlined"
              color="primary"
              onClick={handleToggleDrawer}
              style={{marginLeft: 8}}
              startIcon={<AppsIcon />}
            >
              view: details
            </Button>
          </Tooltip>
        </div>
      </Toolbar>
      <Drawer anchor="right" open={sbsDetails} onClose={handleToggleDrawer}>
        <List dense>
          <ListSubheader style={{color: 'black'}}>Teams</ListSubheader>
          {teams.map((team) => (
            <Tooltip key={team.name} title={`View All ${team.name} SBS`}>
              <ListItem
                key={team.id}
                button
                component="a"
                href={`/#/Sbss?sbsTeamFilterText=${team.name}`}
              >
                <ListItemIcon>
                  <LinkIcon />
                </ListItemIcon>
                <ListItemText primary={team.name} />
              </ListItem>
            </Tooltip>
          ))}
          <ListSubheader style={{color: 'black'}}>Parent SBS</ListSubheader>
          {parentSbss.map((parentSbs) => (
            <Tooltip
              key={parentSbs.id}
              title={`View Parent SBS: ${parentSbs.title}`}
            >
              <ListItem button component="a" href={`/#/Sbss/${parentSbs.id}`}>
                <ListItemIcon>
                  <LinkIcon />
                </ListItemIcon>
                <ListItemText primary={parentSbs.title} />
              </ListItem>
            </Tooltip>
          ))}
          <ListSubheader style={{color: 'black'}}>Tags</ListSubheader>
          {tags.map((tag) => (
            <ListItem key={tag.id}>
              <ListItemText primary={tag.name} />
            </ListItem>
          ))}
        </List>
      </Drawer>
    </Card>
  );
}

SbsProperties.propTypes = {
  sbs: PropTypes.object.isRequired,
  parentSbss: PropTypes.array,
  onDeleteSbs: PropTypes.func,
  duplicationMode: PropTypes.bool,
  onToggleDuplicationMode: PropTypes.func,
  duplicationSelectedSteps: PropTypes.array,
  onToggleSelectAllSteps: PropTypes.func,
  onDuplicateSteps: PropTypes.func,
  sbsTotalTime: PropTypes.string,
  onLoadUnloadAllSubSbs: PropTypes.func.isRequired,
  subSbsLoaded: PropTypes.bool.isRequired,
  hasSubSbs: PropTypes.bool.isRequired,
};

SbsProperties.defaultProps = {
  parentSbss: [],
  onDeleteSbs: undefined,
  duplicationMode: false,
  onToggleDuplicationMode: undefined,
  duplicationSelectedSteps: [],
  onToggleSelectAllSteps: undefined,
  onDuplicateSteps: undefined,
  sbsTotalTime: undefined,
};
